import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { XCircle, RefreshCw, Home, HelpCircle, Phone, AlertTriangle, Clock } from 'lucide-react';
import Header from '../../components/ui/Header';

const reasons = [
  {
    icon: <AlertTriangle size={16} />,
    title: 'Fonds insuffisants',
    description: 'Vérifiez le solde de votre compte Mobile Money ou carte bancaire.',
    accent: '#d97706',
  },
  {
    icon: <Clock size={16} />,
    title: 'Session expirée',
    description: 'La session de paiement a expiré. Relancez le processus depuis la page du bien.',
    accent: '#0891b2',
  },
  {
    icon: <Phone size={16} />,
    title: 'Annulation manuelle',
    description: 'Si vous avez annulé, vous pouvez relancer le paiement à tout moment.',
    accent: '#7c3aed',
  },
];

const PaymentFailure = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const reference = searchParams?.get('reference') || searchParams?.get('trxref') || null;
  const propertyTitle = searchParams?.get('property') || 'Bien immobilier';
  const propertyId = searchParams?.get('property_id') || null;
  const errorMsg = searchParams?.get('message') || null;

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const handleRetry = () => {
    if (propertyId) {
      navigate(`/property/${propertyId}`);
    } else {
      navigate('/property-search-listings');
    }
  };

  return (
    <>
      <Helmet>
        <title>Paiement Échoué – Kapital States</title>
        <meta name="description" content="Votre paiement n'a pas pu être traité. Découvrez comment résoudre le problème et relancer votre réservation." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      <div
        className="min-h-screen text-slate-100 overflow-x-hidden"
        style={{
          fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
          background: '#09090b',
        }}
      >
        <Header />

        {/* Ambient glow */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full opacity-15"
            style={{ background: 'radial-gradient(ellipse, #dc2626 0%, transparent 70%)', filter: 'blur(60px)' }}
          />
        </div>

        <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">

          {/* Failure badge */}
          <div
            className="flex flex-col items-center text-center mb-12"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ background: 'rgba(220,38,38,0.12)', border: '1.5px solid rgba(220,38,38,0.35)' }}
            >
              <XCircle size={40} color="#f87171" strokeWidth={1.5} />
            </div>
            <span
              className="text-xs font-semibold tracking-widest uppercase mb-3"
              style={{ color: '#f87171', letterSpacing: '0.15em' }}
            >
              Paiement non abouti
            </span>
            <h1
              className="text-3xl sm:text-4xl font-bold mb-4 leading-tight"
              style={{ color: '#f8fafc' }}
            >
              Une erreur s'est{' '}
              <span style={{ background: 'linear-gradient(135deg, #f87171, #dc2626)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                produite
              </span>
            </h1>
            <p className="text-slate-400 text-base max-w-md leading-relaxed">
              Votre paiement n'a pas pu être traité. Aucun montant n'a été débité de votre compte. Vous pouvez réessayer en toute sécurité.
            </p>
          </div>

          {/* Error detail card */}
          <div
            className="rounded-2xl mb-8 overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.5s 0.1s cubic-bezier(0.16,1,0.3,1), transform 0.5s 0.1s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            {/* Card header */}
            <div
              className="px-6 py-4 flex items-center gap-3"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(220,38,38,0.06)' }}
            >
              <AlertTriangle size={16} color="#f87171" />
              <span className="text-sm font-semibold text-slate-200">Détails de la tentative</span>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Property */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Bien immobilier</p>
                  <p className="text-slate-100 font-semibold text-base leading-snug">{propertyTitle}</p>
                </div>
                {propertyId && (
                  <Link
                    to={`/property/${propertyId}`}
                    className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors"
                    style={{ background: 'rgba(37,99,235,0.15)', color: '#93c5fd', border: '1px solid rgba(37,99,235,0.25)' }}
                  >
                    Voir le bien
                  </Link>
                )}
              </div>

              <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)' }} />

              {/* Reference */}
              {reference && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Référence</p>
                  <p
                    className="text-xs font-mono px-2.5 py-1 rounded-md"
                    style={{ background: 'rgba(255,255,255,0.06)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    {reference}
                  </p>
                </div>
              )}

              {/* Error message */}
              {errorMsg && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Motif</p>
                  <p className="text-xs text-red-400">{errorMsg}</p>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Statut</p>
                <span
                  className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
                  style={{ background: 'rgba(220,38,38,0.12)', color: '#f87171', border: '1px solid rgba(220,38,38,0.3)' }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />
                  Échoué
                </span>
              </div>

              {/* Safety notice */}
              <div
                className="flex items-start gap-3 rounded-xl p-4 mt-2"
                style={{ background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.2)' }}
              >
                <HelpCircle size={16} color="#60a5fa" className="flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-400 leading-relaxed">
                  <strong className="text-slate-300">Aucun débit n'a été effectué.</strong> Votre compte est en sécurité. Vous pouvez relancer le paiement sans risque.
                </p>
              </div>
            </div>
          </div>

          {/* Possible reasons */}
          <div
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.5s 0.2s cubic-bezier(0.16,1,0.3,1), transform 0.5s 0.2s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-4">Causes possibles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {reasons?.map((r, i) => (
                <div
                  key={i}
                  className="rounded-xl p-4 flex flex-col gap-2"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${r?.accent}18`, color: r?.accent, border: `1px solid ${r?.accent}30` }}
                  >
                    {r?.icon}
                  </div>
                  <p className="text-sm font-semibold text-slate-200">{r?.title}</p>
                  <p className="text-xs text-slate-500 leading-relaxed">{r?.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA buttons */}
          <div
            className="flex flex-col sm:flex-row gap-3 mt-10"
            style={{
              opacity: visible ? 1 : 0,
              transition: 'opacity 0.5s 0.35s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <button
              onClick={handleRetry}
              className="flex-1 flex items-center justify-center gap-2 py-3 px-5 rounded-xl font-semibold text-sm transition-all cursor-pointer"
              style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff' }}
            >
              <RefreshCw size={16} />
              Réessayer le paiement
            </button>
            <Link
              to="/property-search-listings"
              className="flex-1 flex items-center justify-center gap-2 py-3 px-5 rounded-xl font-semibold text-sm transition-all"
              style={{ background: 'rgba(255,255,255,0.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <Home size={16} />
              Explorer d'autres biens
            </Link>
          </div>

          {/* Support note */}
          <p className="text-center text-xs text-slate-600 mt-8">
            Besoin d'aide ? Contactez-nous à{' '}
            <a href="mailto:contact@kapitalstates.com" className="text-slate-400 hover:text-slate-300 transition-colors">
              contact@kapitalstates.com
            </a>{' '}
            ou appelez le{' '}
            <a href="tel:+2250000000000" className="text-slate-400 hover:text-slate-300 transition-colors">
              +225 00 00 00 00 00
            </a>
          </p>
        </main>
      </div>
    </>
  );
};

export default PaymentFailure;
